import {
	Dispatch,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	LocationType,
	VelavuAPI,
	VelavuDevice,
	VelavuFloor,
	VelavuGeofence,
	VelavuSite,
	VelavuStreamUpdateOperation,
	VelavuStreamUpdateTarget,
} from "velavu-js-api";
import AssetCategorization, {
	convertCategoryGroupsToAssetCategorizations,
} from "../data/asset-categorization";
import { useSignInContext } from "../components/sign-in-context/sign-in-context";
import { findArrayDiff } from "./array-helper";
import { useCancellableEffect } from "./hook-helper";
import { useTranslation } from "react-i18next";
import { getLocationFloorInstance } from "./floor-helper";
import { getReadableSiteLocation } from "./language-helper";
import { useVelavuStream } from "velavu-react";
import type { TFunction } from "i18next";
import { mapboxReverseGeocode } from "./mapbox-geocoding-helper";

//Converts a set of coordinates to a human-readable address
export async function requestAddressReverseGeocoding(
	mapboxToken: string,
	language: string,
	coordinates: readonly [number, number],
): Promise<string | undefined> {
	//Perform a reverse-geocoding lookup
	const lookupResult = await mapboxReverseGeocode(mapboxToken, coordinates, {
		language: language,
	});

	//Make sure we have a result
	if (lookupResult.features.length === 0) {
		return undefined;
	}

	//Return the name with its place name, if available
	const featureProperties = lookupResult.features[0].properties;
	const placeName = featureProperties.context?.place?.name;
	if (placeName !== undefined) {
		return featureProperties.name + ", " + placeName;
	} else {
		return featureProperties.name;
	}
}

export async function requestCityReverseGeocoding(
	mapboxToken: string,
	language: string,
	coordinates: readonly [number, number],
): Promise<string | undefined> {
	//Perform a reverse-geocoding lookup
	const lookupResult = await mapboxReverseGeocode(mapboxToken, coordinates, {
		language: language,
		types: "place",
	});
	if (lookupResult.features.length === 0) {
		return undefined;
	}
	return lookupResult.features[0].properties.name;
}

enum DisplayLocationState {
	Checking,
	NoLocation,
	UnknownLocation,
	Approximate,
	Site,
	Coordinates,
	Address,
}

type DisplayLocationValue =
	| { state: DisplayLocationState.Checking }
	| { state: DisplayLocationState.NoLocation }
	| { state: DisplayLocationState.UnknownLocation }
	| { state: DisplayLocationState.Approximate; city: string }
	| {
			state: DisplayLocationState.Site;
			site: VelavuSite;
			floor: VelavuFloor | undefined;
	  }
	| { state: DisplayLocationState.Coordinates; coordinates: [number, number] }
	| { state: DisplayLocationState.Address; address: string };

function formatDisplayLocationValue(
	value: DisplayLocationValue,
	t: TFunction,
): string {
	switch (value.state) {
		case DisplayLocationState.Checking:
			return t("api_helper.checking_location");
		case DisplayLocationState.NoLocation:
			return t("api_helper.no_location");
		case DisplayLocationState.UnknownLocation:
			return t("api_helper.unknown_location");
		case DisplayLocationState.Approximate:
			return t("api_helper.approximate_location", {
				location: value.city,
			});
		case DisplayLocationState.Site:
			return getReadableSiteLocation(value.site, value.floor, t);
		case DisplayLocationState.Coordinates:
			return value.coordinates.join(", ");
		case DisplayLocationState.Address:
			return value.address;
	}
}

export function useGeocoder(coordinates?: [number, number]): string {
	const { t, i18n } = useTranslation();
	const [location, setLocation] = useState<DisplayLocationValue>({
		state: DisplayLocationState.Checking,
	});
	const mapboxToken = useMapboxAPIKey();
	const mapboxLanguage = i18n.language;

	useCancellableEffect(
		(addPromise) => {
			//Ignoring if there's no coordinates
			if (!coordinates) return;

			setLocation({ state: DisplayLocationState.Checking });

			//Getting the mapbox token
			if (mapboxToken === undefined) {
				console.warn("Geocoder failed; no Mapbox token");
				return;
			}

			//Fetching the asset location
			addPromise(
				requestAddressReverseGeocoding(
					mapboxToken,
					mapboxLanguage,
					coordinates,
				),
			)
				.then((text) => {
					if (text !== undefined) {
						setLocation({
							state: DisplayLocationState.Address,
							address: text,
						});
					} else {
						setLocation({
							state: DisplayLocationState.UnknownLocation,
						});
					}
				})
				.catch((error) => {
					//Default to geo coordinates if address couldn't be loaded
					setLocation({
						state: DisplayLocationState.Coordinates,
						coordinates: coordinates,
					});
					console.log(error);
				});
		},
		[coordinates, mapboxToken, mapboxLanguage],
	);

	return useMemo(
		() => formatDisplayLocationValue(location, t),
		[location, t],
	);
}

/**
 * Gets a human-readable display location for a device.
 * The display location of the device may be either the device's
 * site name, its address, or its coordinates.
 * @param device The device to get the display location of
 * @return A human-readable display location of the device
 */
export function useDisplayLocation(device: VelavuDevice | undefined) {
	const { t, i18n } = useTranslation();
	const [display, setDisplay] = useState<DisplayLocationValue>({
		state: DisplayLocationState.Checking,
	});
	const mapboxToken = useMapboxAPIKey();
	const mapboxLanguage = i18n.language;

	useCancellableEffect(
		(addPromise) => {
			setDisplay({ state: DisplayLocationState.Checking });

			addPromise(
				(async (): Promise<DisplayLocationValue> => {
					//Set a fallback string if there is no device
					if (device === undefined) {
						return { state: DisplayLocationState.NoLocation };
					}

					//Cellular isn't very accurate, so only look up
					//the approximate location
					const approximate =
						device?.location?.location_type ===
						LocationType.Cellular;

					//Attempt to look up the device's site
					if (!approximate && device.site_id !== undefined) {
						try {
							const targetSite =
								await VelavuAPI.sites.getSpecificSite(
									device.site_id,
								);

							const floor = getLocationFloorInstance(
								device.location,
								targetSite.floors,
							);
							return {
								state: DisplayLocationState.Site,
								site: targetSite,
								floor,
							};
						} catch (error) {
							console.warn(
								`Failed to fetch site ${device.site_id}`,
								error,
							);
							//Fall through and try device address
						}
					}

					//Attempt to look up the device's address
					if (device.location !== undefined) {
						//Get the Mapbox token
						if (mapboxToken === undefined) {
							console.warn("Geocoder failed; no Mapbox token");
							return { state: DisplayLocationState.NoLocation };
						}

						const coordinates = device.location.coordinates;

						//Fetch the asset location
						try {
							if (approximate) {
								const cityResult =
									await requestCityReverseGeocoding(
										mapboxToken,
										mapboxLanguage,
										coordinates,
									);
								if (cityResult === undefined) {
									return {
										state: DisplayLocationState.UnknownLocation,
									};
								} else {
									return {
										state: DisplayLocationState.Approximate,
										city: cityResult,
									};
								}
							} else {
								const address =
									await requestAddressReverseGeocoding(
										mapboxToken,
										mapboxLanguage,
										coordinates,
									);
								if (address === undefined) {
									return {
										state: DisplayLocationState.UnknownLocation,
									};
								} else {
									return {
										state: DisplayLocationState.Address,
										address: address,
									};
								}
							}
						} catch (error) {
							console.log(error);

							//Default to geo coordinates if address couldn't be loaded
							return {
								state: DisplayLocationState.Coordinates,
								coordinates: coordinates,
							};
						}
					}

					//No way to determine the device's location
					return { state: DisplayLocationState.NoLocation };
				})(),
			)
				.then(setDisplay)
				.catch(console.warn);
		},
		[device, mapboxToken, mapboxLanguage],
	);

	return useMemo(() => formatDisplayLocationValue(display, t), [display, t]);
}

//Loads assets and compiles them into a categorization list for autocomplete
export function useCategorizationSuggestions(): AssetCategorization[] {
	//Load asset metadata from global organization state
	const organizationMetaAssets =
		useSignInContext().organization?.meta?.assets;

	return useMemo(() => {
		if (organizationMetaAssets === undefined) {
			return [];
		} else {
			return convertCategoryGroupsToAssetCategorizations(
				organizationMetaAssets.category_groups,
			);
		}
	}, [organizationMetaAssets]);
}

type PendingDownload = { key: string; id: number };

/**
 * Syncs a selection of string IDs with remote data
 * @param selection The selection of IDs to sync
 * @param downloadParam A parameter that affects the downloaded data
 * @param download A function that returns a promise of the downloaded data
 * @return data An object containing the currently available data, and a callback to manually re-fetch the data.
 */
export function useRemoteSync<R, P>(
	selection: string[],
	downloadParam: P,
	download: (id: string, param: P) => Promise<R>,
): [{ [key: string]: R }, () => Promise<R[]>] {
	const [data, setData] = useState<{ [key: string]: R }>({});

	const prevSelection = useRef<string[]>([]);
	const prevDownloadParam = useRef<P>(downloadParam);
	const pendingDownloadArray = useRef<PendingDownload[]>([]);
	const pendingDownloadID = useRef(0);

	const startDownload = useCallback(
		(key: string) => {
			//Adding a new pending download
			const pendingDownload: PendingDownload = {
				key: key,
				id: pendingDownloadID.current++,
			};
			pendingDownloadArray.current.push(pendingDownload);

			//Downloading the entry
			return download(key, downloadParam).then((result) => {
				//Check if this download hasn't been cancelled
				if (pendingDownloadArray.current.includes(pendingDownload)) {
					//Add the item
					setData((data) => ({ ...data, [key]: result }));
				}

				return result;
			});
		},
		[download, downloadParam],
	);

	useEffect(() => {
		// If the download parameter changed, remove all data and re-download
		if (downloadParam !== prevDownloadParam.current) {
			//Clear all pending downloads
			pendingDownloadArray.current = [];

			//Download all entries
			for (const addedKey of selection) {
				startDownload(addedKey);
			}
		} else {
			//Compare to previous
			const { only1: removed, only2: added } = findArrayDiff(
				prevSelection.current,
				selection,
			);

			if (removed.length > 0) {
				//Delete removed entries
				setData((data) => {
					const newData = { ...data };
					for (const removedKey of removed) {
						delete newData[removedKey];
					}

					return newData;
				});

				//Cancel pending downloads that have been removed
				pendingDownloadArray.current =
					pendingDownloadArray.current.filter(
						(download) => !removed.includes(download.key),
					);
			}

			//Download new entries
			for (const addedKey of added) {
				startDownload(addedKey);
			}
		}

		//Update the previous values
		prevDownloadParam.current = downloadParam;
		prevSelection.current = selection;
	}, [selection, downloadParam, download, startDownload]);

	const invokeDownload = useCallback(() => {
		//Clear all pending downloads
		pendingDownloadArray.current = [];

		//Download all entries
		const promiseArray: Promise<R>[] = [];
		for (const addedKey of selection) {
			const promise = startDownload(addedKey);
			promiseArray.push(promise);
		}

		return Promise.all(promiseArray);
	}, [selection, startDownload]);

	return [data, invokeDownload];
}

export interface PaginatedDownloadResult<R> {
	data: R[];
	continuationToken: string | undefined;
}

/**
 * Default ceiling for event lists when the UI intends to load the full result set.
 */
export const DEFAULT_EVENT_LIST_MAX_ITEMS = 10_000;

export interface PaginatedRemoteSyncStatus {
	syncing: boolean;
	hitMaxItems: boolean;
}

export type PaginatedRemoteSyncResult<R> = [
	data: { [key: string]: R[] },
	invokeDownload: () => Promise<R[][]>,
	status: PaginatedRemoteSyncStatus,
];

/**
 * Syncs a selection of string IDs with remote data, handling pagination automatically.
 * Continuously follows continuation tokens until the total number of downloaded items
 * across all selected IDs meets or exceeds {@link maxItems}.
 * @param selection The selection of IDs to sync
 * @param downloadParam A parameter that affects the downloaded data
 * @param maxItems Maximum number of total fetched items across all selected keys
 * @param download A function that fetches a single page, accepting a continuation token
 * @return A tuple of `[data, invokeDownload, status]`. `status.hitMaxItems` is true when pagination
 * stopped early because the total fetched item count across all selected IDs reached
 * {@link maxItems} while more pages were still available.
 */
export function usePaginatedRemoteSync<R, P>(
	selection: string[],
	downloadParam: P,
	maxItems: number,
	download: (
		id: string,
		param: P,
		continuationToken: string | undefined,
	) => Promise<PaginatedDownloadResult<R>>,
): PaginatedRemoteSyncResult<R> {
	const [data, setData] = useState<{ [key: string]: R[] }>({});
	const dataTotalLength = useMemo(() => {
		return Object.values(data).reduce((acc, curr) => acc + curr.length, 0);
	}, [data]);

	const [syncingDownloads, setSyncingDownloads] = useState(0);

	const dataLengthByKey = useRef(new Map<string, number>());
	const getDataLength = useCallback(() => {
		let total = 0;
		for (const length of dataLengthByKey.current.values()) {
			total += length;
		}

		return total;
	}, []);

	const prevSelection = useRef<string[]>([]);
	const prevDownloadParam = useRef<P>(downloadParam);
	const pendingDownloadArray = useRef<PendingDownload[]>([]);
	const pendingDownloadID = useRef(0);

	const startDownload = useCallback(
		async (key: string): Promise<R[]> => {
			setSyncingDownloads((count) => count + 1);
			//Adding a new pending download
			const pendingDownload: PendingDownload = {
				key: key,
				id: pendingDownloadID.current++,
			};
			pendingDownloadArray.current.push(pendingDownload);

			try {
				const newItems: R[] = [];
				let continuationToken: string | undefined = undefined;
				do {
					const pageResults = await download(
						key,
						downloadParam,
						continuationToken,
					);

					//Break if this download has been cancelled
					if (
						!pendingDownloadArray.current.includes(pendingDownload)
					) {
						break;
					}

					//Add the items
					newItems.push(...pageResults.data);
					setData((data) => {
						let updatedItems: R[];
						if (key in data) {
							updatedItems = [...data[key], ...pageResults.data];
						} else {
							updatedItems = pageResults.data;
						}

						return {
							...data,
							[key]: updatedItems,
						};
					});
					const pageDataLength = pageResults.data.length;
					dataLengthByKey.current.set(
						key,
						(dataLengthByKey.current.get(key) ?? 0) +
							pageDataLength,
					);

					//Store the continuation token for the next lookup
					continuationToken = pageResults.continuationToken;
				} while (
					continuationToken !== undefined &&
					getDataLength() < maxItems
				);

				return newItems;
			} finally {
				setSyncingDownloads((count) => count - 1);
			}
		},
		[download, downloadParam, maxItems],
	);

	useEffect(() => {
		// If the download parameter changed, remove all data and re-download
		if (downloadParam !== prevDownloadParam.current) {
			//Reset download state
			pendingDownloadArray.current = [];
			dataLengthByKey.current.clear();
			setData({});

			//Download all entries
			for (const addedKey of selection) {
				startDownload(addedKey);
			}
		} else {
			//Compare to previous
			const { only1: removed, only2: added } = findArrayDiff(
				prevSelection.current,
				selection,
			);

			if (removed.length > 0) {
				for (const removedKey of removed) {
					dataLengthByKey.current.delete(removedKey);
				}

				//Delete removed entries
				setData((data) => {
					const newData = { ...data };
					for (const removedKey of removed) {
						delete newData[removedKey];
					}

					return newData;
				});

				//Cancel pending downloads that have been removed
				pendingDownloadArray.current =
					pendingDownloadArray.current.filter(
						(download) => !removed.includes(download.key),
					);
			}

			//Download new entries
			for (const addedKey of added) {
				startDownload(addedKey);
			}
		}

		//Update the previous values
		prevDownloadParam.current = downloadParam;
		prevSelection.current = selection;
	}, [selection, downloadParam, download, startDownload]);

	const invokeDownload = useCallback(() => {
		//Clear all pending downloads
		pendingDownloadArray.current = [];
		dataLengthByKey.current.clear();
		setData({});

		//Download all entries
		const promiseArray: Promise<R[]>[] = [];
		for (const addedKey of selection) {
			const promise = startDownload(addedKey);
			promiseArray.push(promise);
		}

		return Promise.all(promiseArray);
	}, [selection, startDownload]);

	return [
		data,
		invokeDownload,
		{
			syncing: syncingDownloads > 0,
			hitMaxItems: dataTotalLength >= maxItems,
		},
	];
}

//Gets the Mapbox API key from the current user
export function useMapboxAPIKey() {
	return useSignInContext().velavuUser?.mapbox_token;
}

export interface SitesState {
	sites: VelavuSite[] | undefined;
	setSites: Dispatch<SetStateAction<VelavuSite[] | undefined>>;
	lookUpSite: (siteID: string | undefined) => VelavuSite | undefined;
	refreshSites: () => Promise<VelavuSite[]>;
}

export function useSites(): SitesState {
	const [sites, setSites] = useState<VelavuSite[] | undefined>(undefined);

	useCancellableEffect((addPromise) => {
		addPromise(VelavuAPI.sites.getAllSites())
			.then(setSites)
			.catch((error) =>
				console.log("There was a sites get error %O", error.response),
			);
	}, []);

	const siteIDMap = useMemo(() => {
		const map = new Map<string, VelavuSite>();

		if (sites !== undefined) {
			for (const site of sites) {
				map.set(site.id, site);
			}
		}

		return map;
	}, [sites]);

	const lookUpSite = useCallback(
		(siteID: string | undefined) => {
			if (siteIDMap === undefined || siteID === undefined) {
				return undefined;
			}

			return siteIDMap.get(siteID);
		},
		[siteIDMap],
	);

	const refreshSites = useCallback(async () => {
		const sites = await VelavuAPI.sites.getAllSites();
		setSites(sites);
		return sites;
	}, [setSites]);

	//Subscribe to site updates
	useVelavuStream(
		VelavuStreamUpdateTarget.Site,
		useCallback(
			(event) => {
				const siteID = event.id;

				switch (event.operation) {
					case VelavuStreamUpdateOperation.Updated:
						setSites((prevSites) => {
							if (prevSites === undefined) {
								return undefined;
							}

							//Find the site in the list
							const siteIndex = prevSites.findIndex(
								(site) => site.id === siteID,
							);
							if (siteIndex === -1) {
								return prevSites;
							}

							//Insert the updated site
							const copy = [...prevSites];
							copy[siteIndex] = event.body;
							return copy;
						});
						break;
					case VelavuStreamUpdateOperation.Created:
						setSites((prevSites) => {
							if (prevSites === undefined) {
								return undefined;
							}

							//Find the site in the list
							const siteIndex = prevSites.findIndex(
								(site) => site.id === siteID,
							);
							if (siteIndex === -1) {
								//Add the site
								return [...prevSites, event.body];
							} else {
								//Update the site
								const copy = [...prevSites];
								copy[siteIndex] = event.body;
								return copy;
							}
						});
						break;
				}
			},
			[setSites],
		),
	);

	return useMemo(
		() => ({
			sites,
			setSites,
			lookUpSite,
			refreshSites,
		}),
		[sites, setSites, lookUpSite, refreshSites],
	);
}

export function useGeofences(): [
	VelavuGeofence[] | undefined,
	Dispatch<SetStateAction<VelavuGeofence[] | undefined>>,
	() => Promise<VelavuGeofence[]>,
] {
	const [geofences, setGeofences] = useState<VelavuGeofence[] | undefined>(
		undefined,
	);

	useCancellableEffect((addPromise) => {
		addPromise(VelavuAPI.geofences.getAllGeofences())
			.then(setGeofences)
			.catch((error) =>
				console.log(
					"There was a geofences get error %O",
					error.response,
				),
			);
	}, []);

	const refresh = useCallback(async () => {
		const geofences = await VelavuAPI.geofences.getAllGeofences();
		setGeofences(geofences);
		return geofences;
	}, [setGeofences]);

	//Subscribe to geofence updates
	useVelavuStream(
		VelavuStreamUpdateTarget.Geofence,
		useCallback(
			(event) => {
				const geofenceID = event.id;

				switch (event.operation) {
					case VelavuStreamUpdateOperation.Updated:
						setGeofences((prevGeofences) => {
							if (prevGeofences === undefined) {
								return undefined;
							}

							//Find the geofence in the list
							const geofenceIndex = prevGeofences.findIndex(
								(geofence) => geofence.id === geofenceID,
							);
							if (geofenceIndex === -1) {
								return prevGeofences;
							}

							//Insert the updated geofence
							const copy = [...prevGeofences];
							copy[geofenceIndex] = event.body;
							return copy;
						});
						break;
					case VelavuStreamUpdateOperation.Created:
						setGeofences((prevGeofences) => {
							if (prevGeofences === undefined) {
								return undefined;
							}

							//Find the geofence in the list
							const geofenceIndex = prevGeofences.findIndex(
								(geofence) => geofence.id === geofenceID,
							);
							if (geofenceIndex === -1) {
								//Add the geofence
								return [...prevGeofences, event.body];
							} else {
								//Update the geofence
								const copy = [...prevGeofences];
								copy[geofenceIndex] = event.body;
								return copy;
							}
						});
						break;
				}
			},
			[setGeofences],
		),
	);

	return [geofences, setGeofences, refresh];
}
